/* eslint-disable max-len */
import { QuestionCircleOutlined, SyncOutlined, CloseOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form, Input,
  Modal,
  Row,
  Spin,
  Tooltip,
} from 'antd';
import { patchGenerarDeclaracion, postDeclaracionCedularRetenedores } from 'api/recaudacion';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import moment from 'moment';
import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { actualizacionTotal, getINPC, recargosTotal } from 'utils/autocalculos';
import { DATE_FORMAT } from 'utils/formatters';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { CURP_PATTERN, RFC_PATTERN } from 'utils/patterns';
import round from 'utils/round';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC valido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP valida.' }],
};

const FormCedularRetenedor = ({
  padron,
  statusDeclaracion,
  visible,
  setVisibleForm,
  isComplementaria,
  selectedCargos,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [porcentajePeriodo, setPorcentajePeriodo] = useState(0);
  const [INPC, setINPC] = useState(0);

  // watchers
  const impuestoDeterminado = Form.useWatch('total_de_impuesto_determinado', form);
  const importeDeActualizacion = Form.useWatch('actualizacion', form);
  const importeRecargos = Form.useWatch('recargos', form);
  const importeMultas = Form.useWatch('multa', form);
  const importeTotalDeContribuciones = Form.useWatch('total_de_contribuciones', form);
  const importePagadoConAnterioridad = Form.useWatch('monto_pagado_con_anterioridad', form);

  const calculateINPC = async () => {
    if (!selectedCargos.length) return;

    const periodo = selectedCargos[0]?.periodo_fiscal?.periodo || '2024';
    const mes = selectedCargos[0]?.identificador_periodo;
    const _inpc = await getINPC(periodo, mes);
    setINPC(_inpc?.[0] ?? {});
  };

  const calcularActualizacion = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ actualizacion: 0 });
        return;
      }

      const actualizacion = await actualizacionTotal({
        importe_cargo: importeNumerico,
        tipo_de_cargo: selectedCargos[0],
        INPC,
      });

      form.setFieldsValue({ actualizacion });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ actualizacion: 0 });
    }
  };

  const calcularRecargos = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);
      const base_gravada = typeof impuestoDeterminado === 'number'
        ? impuestoDeterminado : parseFloat(impuestoDeterminado);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ recargos: 0 });
        return;
      }

      const recargos = await recargosTotal({
        tipo_de_cargo: selectedCargos[0],
        importe_cargo: importeNumerico,
        base_gravada: base_gravada || 0,
        fecha_de_vencimiento: selectedCargos[0]?.fecha_de_vencimiento,
      });
      form.setFieldsValue({ recargos });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ recargos: 0 });
    }
  };

  const calcularTotalDeContribuciones = () => {
    try {
      const valores = [
        impuestoDeterminado,
        importeDeActualizacion,
        importeRecargos,
        importeMultas,
      ].map((valor) => {
        const valorNumerico = typeof valor === 'number' ? valor : parseFloat(valor);
        return Number.isNaN(valorNumerico) ? 0 : valorNumerico;
      });

      const total = valores.reduce((suma, valor) => suma + valor, 0);

      if (total > 0) {
        form.setFieldsValue({ total_de_contribuciones: round(total)[0] });
      }
    } catch (err) {
      _logger(err);
      form.setFieldsValue({ total_de_contribuciones: 0 });
    }
  };

  const calcularCantidadAPagar = () => {
    try {
      const importeTotalDeContribucionesNumerico = typeof importeTotalDeContribuciones === 'number'
        ? (importeTotalDeContribuciones || 0) : parseFloat(importeTotalDeContribuciones || 0);
      const importePagadoConAnterioridadNumerico = typeof importePagadoConAnterioridad === 'number'
        ? (importePagadoConAnterioridad || 0) : parseFloat(importePagadoConAnterioridad || 0);

      const total = importeTotalDeContribucionesNumerico - importePagadoConAnterioridadNumerico;

      if (total > 0) {
        form.setFieldsValue({
          cantidad_a_cargo: round(total)[0],
          cantidad_a_favor: 0,
        });
      } else {
        form.setFieldsValue({
          cantidad_a_favor: round(Math.abs(total))[0],
          cantidad_a_cargo: 0,
        });
      }
    } catch (err) {
      _logger(err);
      form.setFieldsValue({ cantidad_a_cargo: 0 });
    }
  };

  useEffect(() => {
    calcularActualizacion({ importe: impuestoDeterminado });
  }, [impuestoDeterminado]);

  useEffect(() => {
    calcularRecargos({ importe: importeDeActualizacion });
  }, [importeDeActualizacion, impuestoDeterminado]);

  useEffect(() => {
    calcularTotalDeContribuciones();
  }, [
    impuestoDeterminado,
    importeDeActualizacion,
    importeRecargos,
    importeMultas,
  ]);

  useEffect(() => {
    calcularCantidadAPagar();
  }, [importeTotalDeContribuciones, importePagadoConAnterioridad]);

  // Resetea el formulario cuando se cierra.
  useEffect(() => {
    if (visible) {
      calculateINPC();
      const _porcentaje = ((selectedCargos[0]?.importe || 2.5) / 100);
      setPorcentajePeriodo(_porcentaje);
      form.resetFields();
    }
  }, [visible]);

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }
      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo:
        padron?.empresa?.contribuyente?.razon_social
        || padron?.empresa?.contribuyente?.nombre_completo
        || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        entidad: 1,
        fecha_del_pago_realizado_anterior: isComplementaria && !isZeroState ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          base2_de_bienes_inmuebles: 0,
          base1_de_servicios_profesionales: 0,
          retencion1_de_servicios_profesionales: 0,
          retencion2_de_bienes_inmuebles: 0,
          base_del_pago: 0,
          monto_pagado_con_anterioridad: 0,
          total_de_impuesto_determinado: 0,
          actualizacion: 0,
          multa: 0,
          recargos: 0,
          retenciones: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionCedularRetenedores(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validateController = ({ fieldName, message = [] }) => form.validateFields([fieldName])
    .then(() => form.setFields([{ name: fieldName, errors: message || [] }]));

  const validacionesDeOperaciones = (values) => {
    const retencion_rounded = round(parseFloat(values.retenciones) || 0)[0];
    const impuesto_rounded = round(parseFloat(values.impuesto) || 0)[0];
    const total_de_impuesto_determinado = parseInt(values.total_de_impuesto_determinado, 10);

    // ImpuestosCorrespondiente menos Retenciones = ImpuestoCargo(debe ser mayor igual a cero)
    if ((impuesto_rounded - retencion_rounded) < 0 && !isZeroState) {
      validateController({ fieldName: 'retenciones', message: ['El valor no puede ser mayor al impuesto correspondiente.'] });
    } else if ((impuesto_rounded - retencion_rounded) >= 0 && !isZeroState) {
      validateController({ fieldName: 'impuesto_a_cargo', message: [] });
      validateController({ fieldName: 'retenciones', message: [] });
    }

    if (total_de_impuesto_determinado >= 0 && !isZeroState) {
      validateController({ fieldName: 'total_de_impuesto_determinado', message: [] });
    }

    if ((impuesto_rounded - retencion_rounded) <= 0 && !isZeroState) {
      form.setFieldsValue({ actualizacion: 0, recargos: 0, multa: 0 });
    }
  };

  // Calcula el total de la cantidad a pagar.
  const total = () => {
    const values = form.getFieldsValue();

    const actualizacion = parseFloat(values.actualizacion) || 0;
    const recargos = parseFloat(values.recargos) || 0;
    const multas = parseFloat(values.multa) || 0;
    const monto_pagado_con_anterioridad = parseFloat(values.monto_pagado_con_anterioridad) || 0;

    const total_de_impuesto_determinado = [
      parseFloat(values.retencion1_de_servicios_profesionales) || 0,
      parseFloat(values.retencion2_de_bienes_inmuebles) || 0,
    ].reduce((a, b) => parseFloat(a) + parseFloat(b), 0).toFixed(2);

    const total_de_contribuciones_total = [
      parseInt(total_de_impuesto_determinado, 10),
      actualizacion,
      recargos,
      multas,
    ].reduce((a, b) => parseInt(a, 10) + parseInt(b, 10), 0);

    const total_de_contribuciones = total_de_contribuciones_total === 0 ? 0 : total_de_contribuciones_total;

    const cantidad_a_cargo = parseInt((total_de_contribuciones - monto_pagado_con_anterioridad), 10);
    const cantidad_a_pagar_rounded = parseInt(cantidad_a_cargo, 10);

    form.setFieldsValue({ total_de_contribuciones });
    form.setFieldsValue({ total_de_impuesto_determinado: parseInt(total_de_impuesto_determinado, 10) });

    const isNegative = parseInt(total_de_contribuciones, 10) < parseInt(monto_pagado_con_anterioridad, 10);

    if (isNegative) {
      const total_a_favor_rounded = round(parseFloat((monto_pagado_con_anterioridad
        - total_de_contribuciones || 0)));
      form.setFieldsValue({
        cantidad_a_favor: total_a_favor_rounded,
        cantidad_a_cargo: 0,
      });
    } else {
      form.setFieldsValue({
        cantidad_a_cargo: cantidad_a_pagar_rounded,
        cantidad_a_favor: 0,
      });
    }
    validacionesDeOperaciones({
      ...values,
      total_de_impuesto_determinado: parseInt(total_de_impuesto_determinado, 10),
    });
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              DECLARACIÓN MENSUAL RETENEDOR DE IMPUESTOS CEDULARES
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
                icon={<CloseOutlined />}
              >
                <Text>Cancelar</Text>
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                <Text>Generar</Text>
              </Button>
            </Col>
          </Row>
        )}
        width="90vw"
        style={{ maxWidth: '1200px' }}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{
              fecha_del_pago_anterior: moment(),
              es_declaracion_en_ceros: false,
              retencion1_de_servicios_profesionales: 0,
              retencion2_de_bienes_inmuebles: 0,
              base1_de_servicios_profesionales: 0,
              base2_de_bienes_inmuebles: 0,
              base_del_pago: 0,
              recargos: 0,
              multa: 0,
              actualizacion: 0,
              cantidad_a_cargo: 0,
              cantidad_a_favor: 0,
              total_de_contribuciones: 0,
              total_de_impuesto_determinado: 0,
              monto_pagado_con_anterioridad: 0,
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {isZeroState ? (
                <Col xs={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Concepto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan={2} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Text $accent $bold>ENTERO DE RETENCIONES TERCEROS</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            I. Base de la retención del impuesto cedular por la prestación de servicios profesionales
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="base1_de_servicios_profesionales"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onChange={(_value) => {
                                  if (parseInt(_value.target.value, 10) > 0) {
                                    form.setFieldsValue({ retencion1_de_servicios_profesionales: parseInt(_value.target.value * 0.015, 10) });
                                  } else {
                                    form.setFieldsValue({ retencion1_de_servicios_profesionales: 0 });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto servicios profesionales (1.5%)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="retencion1_de_servicios_profesionales"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            II. Base de la retención del impuesto cedular por el goce temporal de bienes inmuebles
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="base2_de_bienes_inmuebles"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onChange={(_value) => {
                                  if (parseInt(_value.target.value, 10) > 0) {
                                    form.setFieldsValue({ retencion2_de_bienes_inmuebles: parseInt(_value.target.value * porcentajePeriodo, 10) });
                                  } else {
                                    form.setFieldsValue({ retencion2_de_bienes_inmuebles: 0 });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Tooltip title={`del periodo fiscal 2019 al 2022 se calcula el 1.5% y 2023 al 2024 ${porcentajePeriodo * 100}%`}>
                              Impuesto arrendamiento
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="retencion2_de_bienes_inmuebles"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Text $accent $bold>PAGO DEL IMPUESTO</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Total de impuesto determinado</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="total_de_impuesto_determinado"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                                ..._rules.mayorCero,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Actualización (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="actualizacion"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Recargos (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="recargos"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Multas (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="multa"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Total de contribuciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="total_de_contribuciones"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        {isComplementaria && (
                          <>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Fecha del pago anterior</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  rules={[_rules.required('El campo es requerido')]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Monto pagado con anterioridad (Monto manual)</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El campo es requerido'),
                                    ..._rules.numeric,
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input prefix="$" />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a pagar</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_cargo"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a favor</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_favor"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormCedularRetenedor.propTypes = {
  padron: propTypes.shape({
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  callbackCancel: propTypes.func,
  declaracionId: propTypes.func.isRequired,
  selectedCargos: propTypes.arrayOf({
    fecha_de_vencimiento: propTypes.string,
    importe: propTypes.number,
    identificador_periodo: propTypes.string,
    periodo_fiscal: propTypes.shape({
      periodo: propTypes.string,
    }),
  }),
};

FormCedularRetenedor.defaultProps = {
  isComplementaria: false,
  visible: false,
  selectedCargos: [],
  callbackCancel: () => {},
};

export default FormCedularRetenedor;
